import { Fragment, useMemo, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Check, Keyboard, MapPin, PackageCheck, ScanLine, X } from 'lucide-react';
import type { PickStopDto } from '@eombor/contracts';
import { Badge, Button, EmptyState, Input, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { useBarcodeScanner } from '../../../shared/scanner/useBarcodeScanner.js';
import { useScannerClaim } from '../../../shared/scanner/scanner-claim.js';
import { useWarehouseLayout } from '../../warehouse/model/warehouse.queries.js';
import { PickRackMap } from './PickRackMap.js';
import type { LayoutRack, LayoutZone } from '../../warehouse/api/warehouse.api.js';
import { usePickPlan } from '../model/handover.queries.js';
import { useHandoverStore } from '../model/handover.store.js';
import {
  allTaken,
  classifyScan,
  handoverLinesOf,
  initialStates,
  takeStop,
  type ScanResult,
  type StopState,
} from '../model/pick-session.js';
import { PickTakeDialog } from './PickTakeDialog.js';

const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/** Ekranda ko'rinadigan xabar — skanerlash natijasidan. */
interface Alert {
  tone: 'danger' | 'warn';
  title: string;
  detail: string | null;
  /** Sabab bilan davom etish taklif qilinadigan o'rin. */
  forceIndex: number | null;
}

const STATUS_TONE: Record<StopState['status'], 'neutral' | 'success'> = {
  pending: 'neutral',
  taken: 'success',
};

/**
 * Bitta TO'XTASH qatori.
 *
 * Marshrut raqami eng chapda va u 3D dagi yonib turgan raqam bilan
 * AYNAN bir xil — ekranning ikki yarmi bitta narsani boshqacha
 * ko'rsatadi, ikki xil narsani emas.
 */
const StopRow = ({
  stop,
  state,
  active,
  onSelect,
}: {
  stop: PickStopDto;
  state: StopState;
  active: boolean;
  onSelect: () => void;
}) => {
  const done = state.status === 'taken';

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'step' : undefined}
        className={cn(
          'flex w-full items-center gap-3 rounded-[var(--radius-sm)] border p-3 text-left',
          'transition-colors duration-[var(--motion-fast)]',
          active ? 'border-accent bg-accent-soft' : 'border-border hover:bg-surface-2',
          /* Yopilgan qator SO'NIQ: u ro'yxatda qoladi («bu yerga bordim»
             degan belgi), lekin e'tibor talab qilmaydi. */
          done ? 'opacity-60' : undefined,
        )}
      >
        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full',
            'text-sm font-semibold tabular-nums',
            done
              ? 'bg-success text-[var(--color-success-contrast)]'
              : active
                ? 'bg-warn text-[var(--color-warn-contrast)]'
                : 'bg-surface-2 text-text-muted',
          )}
        >
          {done ? <Check size={16} aria-hidden /> : stop.order}
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-text">{stop.nomenclatureName}</span>
          <span className="tabular flex items-center gap-2 text-xs text-text-faint">
            <MapPin size={12} aria-hidden />
            {stop.cellCode}
            <span aria-hidden>·</span>
            {stop.batchNo}
          </span>
        </span>

        <span className="flex shrink-0 flex-col items-end gap-1">
          <span className="tabular text-sm font-medium text-text">
            {done ? round3(state.issuedQty ?? 0) : round3(stop.qty)} {unitText(stop.unit)}
          </span>
          <Badge tone={STATUS_TONE[state.status]}>{t(`pick.status_${state.status}`)}</Badge>
        </span>
      </button>
    </li>
  );
};

/**
 * «HOZIR NIMA QILASIZ» — ekranning eng katta elementi.
 *
 * Ilgari ekran faqat ro'yxat va chizmadan iborat edi va skladchi
 * javon oldida turib «endi nimani o'qitay?» degan savol bilan qolardi:
 * javon yorlig'inimi yoki qutidagini. Ikki qadamli skanerlashning butun
 * ma'nosi shu savolga javobda.
 *
 * Kod MONOSPACE va KATTA: odam uni javondagi yorliq bilan ko'z bilan
 * solishtiradi, o'qib emas.
 */
const PickPrompt = ({ stop, done }: { stop: PickStopDto | undefined; done: boolean }) => {
  if (done || stop === undefined) {
    return (
      <div
        className={cn(
          'border-success/30 bg-success-soft flex items-center gap-3',
          'rounded-[var(--radius-lg)] border p-4',
        )}
      >
        <PackageCheck size={22} aria-hidden className="text-success shrink-0" />
        <span className="flex min-w-0 flex-col">
          <span className="text-base font-semibold text-text">{t('pick.promptDone')}</span>
          <span className="text-sm text-text-muted">{t('pick.promptDoneHint')}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="border-warn/30 bg-warn-soft relative overflow-hidden rounded-[var(--radius-lg)] border p-4">
      {/* Yumshoq yorug'lik — kartochka «tirik» ekanini aytadi va ko'zni
          o'ziga tortadi. Ma'lumot emas, shuning uchun `aria-hidden`. */}
      <span
        aria-hidden
        className={cn(
          'bg-warn-soft pointer-events-none absolute -top-16 -right-10 size-40',
          'rounded-[var(--radius-pill)] opacity-60 blur-2xl',
        )}
      />

      <span className="relative flex flex-col gap-2">
        <span className="flex items-center gap-2">
          <ScanLine size={18} aria-hidden className="text-warn shrink-0 motion-safe:animate-pulse" />
          <span className="text-xs font-semibold tracking-wide text-text-muted uppercase">
            {t('pick.promptProduct')}
          </span>
          <Badge tone="warn" className="ml-auto shrink-0">
            {t('pick.stopOf', { order: stop.order })}
          </Badge>
        </span>

        {/* Skaner kutadigan kod — KATTA va monospace: odam uni qutidagi
            yopishqoq bilan o'qib emas, KO'Z bilan solishtiradi. */}
        <span className="tabular text-2xl leading-8 font-semibold break-all text-text">
          {stop.batchNo}
        </span>

        <span className="text-sm text-text">
          {stop.nomenclatureName} · {round3(stop.qty)} {unitText(stop.unit)}
        </span>

        {/*
          QAYERGA borish kerakligi shu yerda qoladi.

          Javon yorlig'ini alohida o'qitish SHART EMAS — qutidagi
          etiketka partiyaniki va reja o'sha partiya qaysi katakda
          turganini allaqachon biladi. Lekin odam o'sha katakni TOPISHI
          kerak, shuning uchun kod ko'rinib turadi.
        */}
        <span className="tabular flex items-center gap-1.5 text-sm font-medium text-text-muted">
          <MapPin size={14} aria-hidden className="shrink-0" />
          {stop.cellCode}
        </span>
      </span>
    </div>
  );
};

export interface PickModeProps {
  requisitionId: string;
  docNo: string;
  /** Qog'ozdagi «ТРЕБОВАНИЕ №» — olib ketuvchining qo'lidagi raqam. */
  formNo: string | null;
  throughPerson: string | null;
  /** Orqaga — skanerlash bosqichiga. */
  onBack: () => void;
  /** Hammasi yig'ildi: miqdorlar va sabablar chaqiruvchiga o'tadi. */
  onDone: (rows: { lineId: string; issuedQty: number; note: string | null }[]) => void;
}

/**
 * YIG'IB OLISH REJIMI — to'liq ekran, 3D va skaner.
 *
 * Nega to'liq ekran: skladchi bu ekranni javon oldida, qo'lqopda va
 * qo'lida skaner bilan ishlatadi. Yon panel, sarlavha va boshqa
 * bo'limlar unga shu paytda umuman kerak emas va faqat chalg'itadi.
 *
 * Nega 3D: katak kodi (`A-03-02-05`) yangi kelgan xodimga hech narsa
 * aytmaydi — u kodni javonlar orasidan qidirib yuradi. Chizmada esa
 * kerakli kataklar YONIB turadi va marshrut raqami yurish tartibini
 * aytadi (zona → stellaj → qator → o'rin), ya'ni bir zonaga ikki marta
 * borilmaydi.
 *
 * Skaner ikki qadamli: avval JAVON yorlig'i, keyin QUTIDAGI etiketka.
 * Bitta skanerlash yetmaydi va bu ataylab — «to'g'ri javonga bordim»
 * bilan «to'g'ri molni oldim» ikki xil savol. Xato javondan olish
 * omborning eng ko'p uchraydigan xatosi.
 */
export const PickMode = ({
  requisitionId,
  docNo,
  formNo,
  throughPerson,
  onBack,
  onDone,
}: PickModeProps) => {
  /* Skaner SHU ekranniki: qobiqdagi umumiy tinglovchi (katak kartochkasini
     ochadi) jim tursin, aks holda bitta o'qish ikki joyda ishlab ketardi. */
  useScannerClaim();

  const plan = usePickPlan(requisitionId);
  const stops = useMemo(() => plan.data?.stops ?? [], [plan.data]);

  /* Seans DO'KONDA: sahifa qayta yuklanganda yig'ib olingan o'rinlar
     yo'qolmasligi kerak — skladchi javonlar bo'ylab ikkinchi marta
     yurmasin. */
  const session = useHandoverStore((state) => state.pickSession);
  const setSession = useHandoverStore((state) => state.setPickSession);
  const [active, setActive] = useState<number | null>(null);
  const [alert, setAlert] = useState<Alert | null>(null);
  const [taking, setTaking] = useState<{ index: number; force: boolean } | null>(null);
  const [manual, setManual] = useState('');

  /**
   * Seans kaliti — REJANING O'ZIGINASI.
   *
   * Uzunlik yetmaydi: bitta o'rin chiqib, o'rniga boshqasi qo'shilsa son
   * o'zgarmaydi, lekin holatlar boshqa qatorga yopishib qolardi —
   * skladchi olmagan mahsulot «olindi» bo'lib ko'rinardi.
   */
  const key = useMemo(
    () => stops.map((stop) => `${stop.lineId}:${stop.cellId}:${stop.batchId}`).join('|'),
    [stops],
  );

  /* Holat RENDER paytida moslanadi (`useEffect` emas): reja kelishi
     bilan ro'yxat to'g'ri holat bilan chizilsin, bir lahza bo'sh
     turmasin. Kalit o'zgarmagani uchun takroriy sikl bo'lmaydi. */
  const current = session?.key === key ? session.states : initialStates(stops);
  if (session?.key !== key) setSession({ key, states: current });

  const doneCount = current.filter((state) => state.status === 'taken').length;
  const ready = allTaken(current);

  /** Skanerlash kutilayotgan o'rin — xabarlarda «kutilgan» shu bo'ladi. */
  const expected = useMemo(
    () => current.findIndex((state) => state.status !== 'taken'),
    [current],
  );

  const layout = useWarehouseLayout(plan.data?.warehouseId ?? null);

  /* Javon ZONASI bilan birga: sarlavhada katak kodining birinchi bo'lagi
     turadi va skladchi yo'lakni o'shandan taniydi. */
  const racks = useMemo<{ rack: LayoutRack; zone: LayoutZone }[]>(
    () => (layout.data?.zones ?? []).flatMap((zone) => zone.racks.map((rack) => ({ rack, zone }))),
    [layout.data],
  );

  /**
   * Chizmada ko'rinadigan JAVON — KUTILAYOTGAN to'xtashniki.
   *
   * Javon O'ZI almashadi: bir o'rin yopilishi bilan keyingisi qaysi
   * javonda bo'lsa, chizma o'sha javonga o'tadi. Skladchi ro'yxatga
   * qaytib, javonni qo'lda tanlashi kerak emas — ekran «endi ana u
   * yerga boring» deb o'zi aytadi.
   *
   * `active` — qo'lda tanlov (skladchi ro'yxatdan yoki chizmadan
   * bosgan) va u KUTILAYOTGANIDAN ustun turadi; yangi skanerlashda esa
   * yana bo'shaydi.
   */
  const focusIndex = active ?? (expected >= 0 ? expected : 0);

  const facade = useMemo(() => {
    const wanted = stops[focusIndex]?.cellId ?? null;
    return racks.find((row) => row.rack.cells.some((cell) => cell.id === wanted)) ?? null;
  }, [racks, stops, focusIndex]);

  /**
   * Chizmadagi to'xtashlar — FAQAT joriy javondagilar.
   *
   * Boshqa javondagilar bu xaritaga kirmaydi: ular hech qachon
   * chizilmaydi-yu, xarita esa «bu katakda ish bor» deb turardi.
   */
  const stopsOnRack = useMemo<ReadonlyMap<string, { stop: PickStopDto; taken: boolean }>>(() => {
    const map = new Map<string, { stop: PickStopDto; taken: boolean }>();
    if (facade === null) return map;
    for (const [index, stop] of stops.entries()) {
      if (!facade.rack.cells.some((cell) => cell.id === stop.cellId)) continue;
      map.set(stop.cellId, { stop, taken: current[index]?.status === 'taken' });
    }
    return map;
  }, [stops, current, facade]);

  const apply = (result: ScanResult): void => {
    switch (result.kind) {
      case 'productOk':
        setAlert(null);
        setActive(result.index);
        setTaking({ index: result.index, force: false });
        return;
      case 'focus':
        /* Javon yorlig'i — o'rinni YOPMAYDI, faqat diqqatni ko'chiradi:
           skladchi to'g'ri javonga borganini ekranda ko'radi. Yopadigan
           narsa qutidagi etiketka. */
        setAlert(null);
        setActive(result.index);
        return;
      case 'alreadyDone':
        setAlert({
          tone: 'warn',
          title: t('pick.alertAlreadyDone'),
          detail: null,
          forceIndex: null,
        });
        return;
      case 'unknown': {
        const stop = expected < 0 ? undefined : stops[expected];
        setAlert({
          tone: 'danger',
          title: t('pick.alertWrong'),
          /* Kutilgan NOMI ham, skanerlangan KOD ham yoziladi: ikkovisiz
             skladchi xatoni topa olmaydi — «boshqa mahsulot» degan
             xabarning o'zi hech narsa aytmaydi. */
          detail:
            stop === undefined
              ? t('pick.alertWrongCode', { code: result.code })
              : t('pick.alertWrongPair', { name: stop.nomenclatureName, code: result.code }),
          forceIndex: expected < 0 ? null : expected,
        });
        return;
      }
    }
  };

  const scan = (code: string): void => {
    if (stops.length === 0) return;
    apply(classifyScan(stops, current, code));
  };

  useBarcodeScanner(scan);

  const submitManual = (event: FormEvent): void => {
    event.preventDefault();
    if (manual.trim() === '') return;
    scan(manual);
    setManual('');
  };

  const take = (issuedQty: number, note: string | null): void => {
    if (taking === null) return;
    setSession({
      key,
      states: takeStop(current, taking.index, issuedQty, note),
    });
    setTaking(null);
    setAlert(null);
    /* Qo'lda tanlov BO'SHAYDI: chizma keyingi kutilayotgan o'ringa
       o'zi o'tsin — skladchi javonni qo'lda qidirmaydi. */
    setActive(null);
  };

  const body = (): React.ReactNode => {
    if (plan.isPending) return <Skeleton lines={6} />;
    if (plan.isError) {
      return (
        <EmptyState
          icon={<ScanLine size={20} aria-hidden />}
          title={t('pick.failed')}
          description={t('pick.failedHint')}
        />
      );
    }
    if (stops.length === 0) {
      return (
        <EmptyState
          icon={<MapPin size={20} aria-hidden />}
          title={t('pick.empty')}
          description={t('pick.emptyHint')}
        />
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <PickPrompt stop={expected < 0 ? undefined : stops[expected]} done={ready} />

        {/*
          Ro'yxat JAVON bo'yicha guruhlanadi.

          Marshrut raqami (`1-o'rin`, `2-o'rin`) yurish tartibini
          aytadi — zona → stellaj → qator → o'rin. Lekin raqamning
          O'ZIDAN qaysi javonga borish kerakligi ko'rinmasdi va
          skladchi har o'rin uchun katak kodini o'qib, javonni undan
          ajratib olishga majbur edi. Sarlavha esa yo'lni bir qarashda
          beradi: avval 01-stellaj, keyin 02, keyin 03.
        */}
        <ul className="flex flex-col gap-2">
          {stops.map((stop, index) => (
            <Fragment key={`${stop.lineId}-${stop.cellId}-${stop.batchId}`}>
              {/* Javon sarlavhasi — yangi javon BOSHLANGANDA. Har qatorda
                  takrorlansa, u ma'lumot emas, shovqin bo'lardi. */}
              {index === 0 ||
              stops[index - 1]?.rackCode !== stop.rackCode ||
              stops[index - 1]?.zoneCode !== stop.zoneCode ? (
                <li className="flex items-center gap-2 pt-2 first:pt-0">
                  <MapPin size={13} aria-hidden className="text-text-faint shrink-0" />
                  <span className="text-xs font-semibold tracking-wide text-text-muted uppercase">
                    {t('pick.rackTitle', { zone: stop.zoneCode, rack: stop.rackCode })}
                  </span>
                  <span aria-hidden className="bg-border h-px min-w-4 flex-1" />
                </li>
              ) : null}

              <StopRow
                stop={stop}
                state={current[index] ?? { status: 'pending', issuedQty: null, note: null }}
                active={active === index}
                onSelect={() => setActive(index)}
              />
            </Fragment>
          ))}
        </ul>
      </div>
    );
  };

  /*
   * PORTAL — `document.body` ga.
   *
   * `position: fixed` eng yaqin O'GIRILGAN (`transform`) ota-elementga
   * nisbatan hisoblanadi, sahifa esa animatsiya bilan chiziladi. Shu
   * sababli oyna qobiq ichida qolib, tepada sarlavha turar va chap
   * chekkasi ekrandan chiqib ketardi — brauzerda aynan shunday
   * ko'rindi. Portal uni qobiqdan butunlay chiqaradi.
   *
   * `z-index` qobiq sarlavhasidan YUQORI, lekin MODALDAN past
   * (`--z-overlay`): bu to'liq ekranli SIRT va uning ustida miqdor
   * oynasi ochiladi.
   */
  return createPortal(
    <div className="bg-bg fixed inset-0 z-[var(--z-overlay)] flex flex-col">
      <header className="border-border flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <X size={16} aria-hidden />
          {t('pick.exit')}
        </Button>

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex items-center gap-2">
            <Badge tone="accent" mono>
              {formNo ?? docNo}
            </Badge>
            <span className="truncate text-sm text-text-muted">
              {throughPerson ?? t('pick.noTaker')}
            </span>
          </span>
          <span className="tabular text-xs text-text-faint">
            {t('pick.progress', { done: doneCount, total: stops.length })}
          </span>
        </span>

        {/* Qo'lda kiritish — skaner ishlamay qolgan kun uchun zaxira yo'l,
            shuning uchun ikkinchi darajali ko'rinishda. */}
        <form onSubmit={submitManual} className="flex items-center gap-2">
          <Input
            aria-label={t('pick.manual')}
            variant="filter"
            mono
            placeholder={t('pick.manualHint')}
            value={manual}
            onChange={(event) => setManual(event.target.value)}
            prefix={<Keyboard size={14} aria-hidden />}
          />
          <Button type="submit" variant="secondary" size="sm" disabled={manual.trim() === ''}>
            {t('pick.manualSubmit')}
          </Button>
        </form>
      </header>

      {alert === null ? null : (
        <div
          role="alert"
          className={cn(
            'flex flex-wrap items-center gap-3 border-b px-4 py-3',
            alert.tone === 'danger'
              ? 'border-danger/30 bg-danger-soft'
              : 'border-warn/30 bg-warn-soft',
          )}
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-semibold text-text">{alert.title}</span>
            {alert.detail === null ? null : (
              <span className="text-sm text-text-muted">{alert.detail}</span>
            )}
          </span>

          <Button variant="ghost" size="sm" onClick={() => setAlert(null)}>
            {t('pick.retry')}
          </Button>

          {/* Qat'iy to'siq QO'YILMAYDI: etiketka yirtiladi, mol qo'lda
              ko'chiriladi — bular kundalik hol va to'siq skladchini
              tizimni chetlab o'tishga majburlardi. Sabab esa yoziladi. */}
          {alert.forceIndex === null ? null : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setTaking({ index: alert.forceIndex ?? 0, force: true });
                setAlert(null);
              }}
            >
              {t('pick.force')}
            </Button>
          )}
        </div>
      )}

      <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[24rem_1fr]">
        <section className="min-h-0 overflow-y-auto p-4">{body()}</section>

        {/*
          JAVON XARITASI — 2D, o'ng tomonda.

          Ombor ko'rinishi ham, quruvchining chizmasi ham emas
          (`PickRackMap` izohiga qarang): skladchining yagona savoli
          «qaysi katak?» va xarita faqat shunga javob beradi.

          Javon O'ZI almashadi — bir o'rin yopilishi bilan keyingisi
          qaysi javonda bo'lsa, chizma o'sha javonga o'tadi. Qo'lda
          tanlagich yo'q va kerak ham emas: skladchi ekran aytgan joyga
          boradi, javon qidirmaydi.
        */}
        <section className="border-border hidden min-h-0 overflow-y-auto border-l p-4 lg:block">
          {layout.isPending ? (
            <Skeleton lines={8} />
          ) : facade === null ? (
            <EmptyState
              icon={<MapPin size={20} aria-hidden />}
              title={t('pick.noRack')}
              description={t('pick.noRackHint')}
            />
          ) : (
            <PickRackMap
              rack={facade.rack}
              zoneCode={facade.zone.code}
              stops={stopsOnRack}
              targetCellId={expected < 0 ? null : (stops[expected]?.cellId ?? null)}
              onSelectCell={(cellId) => {
                const index = stops.findIndex((stop) => stop.cellId === cellId);
                if (index >= 0) setActive(index);
              }}
            />
          )}
        </section>
      </div>

      <footer className="border-border flex items-center justify-between gap-3 border-t px-4 py-3">
        <span className="text-sm text-text-muted">
          {ready ? t('pick.readyHint') : t('pick.scanHint')}
        </span>
        <Button
          disabled={!ready}
          onClick={() =>
            onDone(
              handoverLinesOf(stops, current).map((row) => ({
                lineId: row.lineId,
                issuedQty: row.issuedQty,
                note: row.note ?? null,
              })),
            )
          }
        >
          <PackageCheck size={16} aria-hidden />
          {t('pick.finish')}
          <ArrowRight size={16} aria-hidden />
        </Button>
      </footer>

      <PickTakeDialog
        open={taking !== null}
        stop={taking === null ? null : (stops[taking.index] ?? null)}
        forceReason={taking?.force ?? false}
        onClose={() => setTaking(null)}
        onTake={take}
      />
    </div>,
    document.body,
  );
};
